import React from 'react';
import PropTypes from 'prop-types';
import { Table } from 'reactstrap';
import { Link } from 'react-router-dom';

import Loading from 'components/App/Loading';
import Button from 'components/App/Button';
import CustomerAccountTypeBadge from 'components/App/CustomerAccountTypeBadge';

import CustomerPropTypes from 'propTypeModels/customer';

import { LOCATIONS } from 'constants/index';
import { convertIsoDateToString } from 'utils';

import styles from './CustomerTable.module.scss';

const CustomerAvatar = ({ src, name }) => {
  const [hasError, setHasError] = React.useState(false);

  if (src && !hasError) {
    return (
      <img
        src={src}
        alt="avatar"
        className="AvatarCircle"
        style={{ objectFit: 'cover' }}
        onError={() => setHasError(true)}
      />
    );
  }

  return (
    <div className="AvatarCircle">
      {name ? name.trim().substring(0, 2).toUpperCase() : 'KH'}
    </div>
  );
};

CustomerAvatar.propTypes = {
  src: PropTypes.string,
  name: PropTypes.string,
};

CustomerAvatar.defaultProps = {
  src: '',
  name: '',
};

function CustomerTable({
  data,
  loading,
  onClickBlockUser,
  onClickUnblockUser,
  onClickUpgradeUser,
}) {
  return (
    <div className={styles.Component}>
      <Loading loading={loading} loaderOnPage />
      <Table responsive className="Table">
        <thead className="Table__Header">
          <tr className="Table__Header__Row">
            <th
              className="Table__Header__Row__Content"
              style={{ minWidth: 270 }}
            >
              Mã khách hàng | Tên khách hàng
            </th>
            <th
              className="Table__Header__Row__Content"
              style={{ minWidth: 140 }}
            >
              Loại tài khoản
            </th>
            <th
              className="Table__Header__Row__Content"
              style={{ minWidth: 140 }}
            >
              Sinh nhật
            </th>
            <th
              className="Table__Header__Row__Content"
              style={{ minWidth: 270 }}
            >
              Email
            </th>
            <th
              className="Table__Header__Row__Content"
              style={{ minWidth: 140 }}
            >
              Số điện thoại
            </th>
            <th
              className="Table__Header__Row__Content"
              style={{ minWidth: 160 }}
            >
              Khu vực sinh sống
            </th>
            <th
              className="Table__Header__Row__Content"
              style={{ minWidth: 140 }}
            >
              Ví xu
            </th>
            <th
              className="Table__Header__Row__Content"
              style={{ minWidth: 160 }}
            >
              Đêm phòng đã đặt
            </th>
            <th
              className="Table__Header__Row__Content"
              style={{ minWidth: 140 }}
            >
              Lượt đổi quà
            </th>
            <th
              className="Table__Header__Row__Content"
              style={{ minWidth: 200 }}
            >
              Lần đăng nhập cuối
            </th>
          </tr>
        </thead>

        <tbody className="Table__Body">
          {data && data.length > 0 ? (
            data.map((item) => (
              <tr className="Table__Body__Row" key={item._id}>
                <td className="Table__Body__Row__Content">
                  <div className="d-flex align-items-start">
                    <CustomerAvatar
                      src={item.avatar}
                      name={item.fullName || item.displayName}
                    />

                    <div className="d-flex flex-column" style={{ gap: '12px' }}>
                      <div>
                        <div className="CustomerIDRow">
                          <p className="CustomerID">{`#${item.userId}`}</p>
                          {item.isBlocked && (
                            <p className="IsBlocked">Đã chặn</p>
                          )}
                        </div>
                        <p className="CustomerName">
                          {item.fullName || item.displayName || '—'}
                        </p>
                      </div>
                      {item.isBlocked ? (
                        <div className="Action">
                          <div className="d-flex gap-3 mt-1">
                            <Link to={`${LOCATIONS.CUSTOMERS}/${item._id}`}>
                              <Button
                                outline
                                classes="Action__Button Action__Button__Link"
                              >
                                Xem chi tiết
                              </Button>
                            </Link>

                            <Button
                              outline
                              value={item._id}
                              classes="Action__Button Action__Button__Danger"
                              onClick={onClickUnblockUser}
                            >
                              Bỏ chặn
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="Action">
                          <div className="d-flex gap-3 mt-1">
                            <Button
                              outline
                              classes="Action__Button Action__Button__Link"
                              onClick={() => onClickUpgradeUser(item)}
                            >
                              Nâng cấp tài khoản
                            </Button>

                            <Link to={`${LOCATIONS.CUSTOMERS}/${item._id}`}>
                              <Button
                                outline
                                classes="Action__Button Action__Button__Link"
                              >
                                Xem chi tiết
                              </Button>
                            </Link>

                            <Button
                              outline
                              value={item._id}
                              classes="Action__Button Action__Button__Danger"
                              onClick={onClickBlockUser}
                            >
                              Chặn
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </td>

                <td className="Table__Body__Row__Content">
                  <CustomerAccountTypeBadge level={item.level} />
                </td>

                <td className="Table__Body__Row__Content">
                  {item.birthday || '-'}
                </td>

                <td className="Table__Body__Row__Content">
                  {item.email || '-'}
                </td>

                <td className="Table__Body__Row__Content">
                  {item.phoneNum || item.phone || '-'}
                </td>

                <td className="Table__Body__Row__Content">
                  {item.province || '-'}
                </td>

                <td className="Table__Body__Row__Content">
                  <span className="CoinsText">
                    <strong>{(item.coin || 0).toLocaleString('vi-VN')}</strong>
                    {' '}
                    xu
                  </span>
                </td>

                <td className="Table__Body__Row__Content">
                  {item.bookedNights || 0}
                </td>

                <td className="Table__Body__Row__Content">
                  {item.redeemedGifts ?? 0}
                </td>

                <td className="Table__Body__Row__Content">
                  {item.lastLoginAt
                    ? convertIsoDateToString(item.lastLoginAt, 'DD/MM/YYYY HH:mm:ss')
                    : '-'}
                </td>
              </tr>
            ))
          ) : (
            <tr className="Table__Body__Row">
              <td
                colSpan="9"
                className="Table__Body__Row__Content text-center"
                style={{ borderBottom: 'none' }}
              >
                <div className="NoData">Chưa có dữ liệu</div>
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );
}

CustomerTable.propTypes = {
  loading: PropTypes.bool.isRequired,
  data: CustomerPropTypes.customers.isRequired,
  onClickBlockUser: PropTypes.func.isRequired,
  onClickUnblockUser: PropTypes.func.isRequired,
  onClickUpgradeUser: PropTypes.func.isRequired,
};

export default React.memo(CustomerTable);
